import React from 'react';
import FeeBreakdownModal from './Internal/FeeBreakdownModal';
import { PricingCalculator } from '@eb/pricing-calculator';
import gettext from '@eb/gettext';
import {
    FeeBreakdownInterface,
    PACKAGE_ONE,
    PACKAGE_TWO,
    PackageTypes,
} from '../constants/pricing';
import PricingCalculatorForm from './Internal/PricingCalculatorForm';
import PricingCalculatorBanner from './Internal/PricingCalculatorBanner';

const FREE_EVENT_TOTAL_TEXT: string = gettext(
    'Eventbrite is free to use for free events!',
);
const FREE_ATTENDEE_TOTAL_TEXT = '— —';
const getOppositeAssortment = (currentAssortment: any) =>
    currentAssortment === PACKAGE_ONE ? PACKAGE_TWO : PACKAGE_ONE;

interface PricingCalcSectionProps {
    currencySymbol: string;
    env: {
        currencyFormat: string;
        defaultCurrency: string;
    };
    essentialsDescription: string;
    essentialsPackageBannerText: string;
    header: string;
    localeDefaultPlan: string;
    professionalDescription: string;
    professionalPackageBannerText: string;
    user: {
        isAuthenticated: boolean;
    };
    userPlan: PackageTypes;
}

interface PricingCalcProps {
    assortment: PackageTypes;
    attendeeTotal: string;
    calculateFees: Function;
    currencySymbol: string;
    eventTotal: string;
    feeBreakdown: FeeBreakdownInterface;
    handleInputChange: Function;
    header: string;
    impossiblePriceEncountered: boolean;
    isFree: boolean;
    isLoading: boolean;
    passOnFees: boolean;
    ticketPrice: string;
    ticketPriceError: string;
    ticketPriceProps: object;
    ticketQuantityProps: object;
    ticketQuantity: string;
    ticketQuantityError: string;
    updateEventState: Function;
    updateTicketPriceState: Function;
}

const PricingCalculatorModule = (props: PricingCalcSectionProps) => {
    const {
        currencySymbol = '$',
        localeDefaultPlan,
        env: { currencyFormat, defaultCurrency },
        essentialsDescription,
        essentialsPackageBannerText,
        header,
        professionalDescription,
        professionalPackageBannerText,
        user: { isAuthenticated },
        userPlan,
    } = props;
    return (
        <PricingCalculator
            assortment={localeDefaultPlan}
            defaultCurrency={defaultCurrency}
            currencyFormat={currencyFormat}
            currencySymbol={currencySymbol}
        >
            {({
                assortment,
                attendeeTotal,
                handleInputChange,
                calculateFees,
                eventTotal,
                feeBreakdown,
                impossiblePriceEncountered,
                isFree,
                isLoading,
                passOnFees,
                ticketPrice,
                ticketPriceError,
                ticketPriceProps,
                ticketQuantity,
                ticketQuantityError,
                updateEventState,
                updateTicketPriceState,
            }: PricingCalcProps) => {
                const showChangePlanCTA =
                    isAuthenticated && userPlan && userPlan !== assortment;

                const formProps = {
                    assortment,
                    attendeeTotal: isFree
                        ? FREE_ATTENDEE_TOTAL_TEXT
                        : attendeeTotal,
                    calculateFees,
                    currencySymbol,
                    essentialsDescription,
                    eventTotal: isFree ? FREE_EVENT_TOTAL_TEXT : eventTotal,
                    handleInputChange,
                    header,
                    impossiblePriceEncountered,
                    isFree,
                    isLoading,
                    passOnFees,
                    professionalDescription,
                    showChangePlanCTA,
                    ticketPrice,
                    ticketPriceError,
                    ticketPriceProps,
                    ticketQuantity,
                    ticketQuantityError,
                    updateEventState,
                    updateTicketPriceState,
                };

                const bannerProps = {
                    currentPackage: assortment,
                    essentialsPackageBannerText,
                    handleCTAClick: () => {
                        updateEventState(
                            { assortment: getOppositeAssortment(assortment) },
                            calculateFees,
                        );
                    },
                    professionalPackageBannerText,
                };

                const feeBreakdownWithTotal = {
                    ...feeBreakdown,
                    attendeeTotal,
                    passOnFees,
                };

                return (
                    <div>
                        <FeeBreakdownModal {...feeBreakdownWithTotal}>
                            {(openModal: Function) => (
                                <div>
                                    <PricingCalculatorForm
                                        {...formProps}
                                        openModal={() => {
                                            openModal();
                                        }}
                                    />
                                    <PricingCalculatorBanner {...bannerProps} />
                                </div>
                            )}
                        </FeeBreakdownModal>
                    </div>
                );
            }}
        </PricingCalculator>
    );
};
export default PricingCalculatorModule;
